/**
 * Parse UTC date string to Date object
 * Handles two formats:
 * 1. Simple format: yyyy-mm-dd[SPACE/T]hh:mm (e.g., "2025-09-28 09:45")
 * 2. Full ISO format: yyyy-mm-ddTHH:mm:ss.sssZ (e.g., "2025-09-14T03:39:26.113Z")
 * @param {string} date - UTC date string
 * @returns {Date | null} parsed Date object or null if invalid
 */
export const parseISOSimple = (date: string): Date | null => {
  if (!date) return null

  // Check if it's a full ISO string with timezone info (ends with Z or has timezone offset)
  if (date.includes('T') && (date.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(date))) {
    // Use native Date constructor for full ISO strings - it handles UTC correctly
    const parsedDate = new Date(date)
    return isNaN(parsedDate.getTime()) ? null : parsedDate
  }

  // Handle simple format: yyyy-mm-dd[SPACE/T]hh:mm
  const matches = date.match(/^(\d{4})-(\d{2})-(\d{2})[\sTt](\d{2}):(\d{2})/)

  if (!matches) {
    return null
  }

  // Parse as UTC date for simple format (no timezone info, assume UTC)
  const utcTime = Date.UTC(
    parseInt(matches[1]),
    parseInt(matches[2]) - 1, // months are 0 based in javascript
    parseInt(matches[3]),
    parseInt(matches[4]),
    parseInt(matches[5]),
  )

  return new Date(utcTime)
}

/**
 * Format a date to an AU locale string, using a given format
 * The input date is expected to be UTC and will be displayed in the user's local timezone
 * Supports both simple format (yyyy-mm-dd hh:mm) and full ISO format (yyyy-mm-ddTHH:mm:ss.sssZ)
 *
 * Also exists for use in templates :
 *
 * ```js
 * {{ myDate | dateFormat({some: 'format'...}) }}
 * ```
 *
 *
 * @param {string} date The UTC Date string to format
 * @param {Intl.DateTimeFormatOptions} format A format object to apply
 *
 * Format reference:
 * https://cgi.cse.unsw.edu.au/~cs2041/doc/MDN_javascript_reference/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString.html
 *
 * @returns {String} Formatted date string in user's local timezone
 */
export const dateFormat = (
  date: string,
  format: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long' },
): string => {
  if (!date) return ''

  const parsedDateTime = parseISOSimple(date)

  if (!parsedDateTime) return ''

  // The parsed date is already a Date object representing the UTC time
  // note that toLocaleDateString automatically converts to user's local timezone, unless an alternate timezone is specified in options
  return parsedDateTime.toLocaleDateString('en-AU', format)
}

export const timeFormat = (
  date: string,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' },
): string => {
  if (!date) return ''

  const parsedDateTime = parseISOSimple(date)

  if (!parsedDateTime) return ''

  // The parsed date is already a Date object representing the UTC time
  // note that toLocaleTimeString automatically converts to user's local timezone, unless an alternate timezone is specified in options
  return parsedDateTime.toLocaleTimeString('en-AU', options)
}
